import { useState } from 'react';
import { PhoneCall, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import CallHistory from './CallHistory';

/**
 * Inline "call count" badge. Click opens a modal showing full call history
 * for the given prospect / client / phone.
 *
 * IMPORTANT: Radix Dialog is rendered through a Portal, but React synthetic
 * events still bubble through the React virtual tree (not the DOM tree).
 * Without stopPropagation, clicking inside the dialog — or on its overlay to
 * close it — would bubble up to a parent `<TableRow onClick>` and trigger
 * unwanted navigation. We stop propagation at every entry point.
 */
export default function CallsBadge({ item, size = 'md' }) {
    const [open, setOpen] = useState(false);
    const total = item?.calls_count || 0;
    const inn = item?.calls_incoming || 0;
    const out = item?.calls_outgoing || 0;

    const iconSize = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3';

    // Wrapper that swallows any click / pointer event coming from children
    // (including the portaled Dialog overlay & content via React bubbling).
    const stop = (e) => e.stopPropagation();

    return (
        <span
            onClick={stop}
            onMouseDown={stop}
            onPointerDown={stop}
            onPointerUp={stop}
            className="inline-flex"
        >
            {total === 0 ? (
                <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-[#1B365D] transition-colors"
                    onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                    data-testid="calls-badge-empty"
                    title="Aucun appel — cliquer pour vérifier"
                >
                    —
                </button>
            ) : (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1B365D]/5 border border-[#1B365D]/10 hover:bg-[#1B365D]/10 hover:border-[#1B365D]/20 transition-colors cursor-pointer"
                    title={`${inn} entrant(s), ${out} sortant(s) — cliquer pour voir l'historique`}
                    data-testid={`calls-badge-${item?.id || 'row'}`}
                >
                    <PhoneCall className={`${iconSize} text-[#1B365D]`} />
                    <span className="text-xs font-semibold text-[#1B365D]">{total}</span>
                    {inn > 0 && (
                        <span className="flex items-center gap-0.5 text-emerald-600 text-[10px] font-semibold">
                            <PhoneIncoming className="w-2.5 h-2.5" /> {inn}
                        </span>
                    )}
                    {out > 0 && (
                        <span className="flex items-center gap-0.5 text-blue-600 text-[10px] font-semibold">
                            <PhoneOutgoing className="w-2.5 h-2.5" /> {out}
                        </span>
                    )}
                </button>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="max-w-lg max-h-[85vh] overflow-hidden p-0"
                    data-testid="calls-history-modal"
                    onClick={stop}
                    onMouseDown={stop}
                    onPointerDown={stop}
                    onPointerDownOutside={stop}
                    onInteractOutside={stop}
                >
                    <DialogHeader className="px-5 pt-5">
                        <DialogTitle className="text-[#1B365D]">
                            Historique des appels {item?.nom ? `— ${item.nom}` : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="px-5 pb-5 pt-2 overflow-y-auto max-h-[70vh]">
                        <CallHistory
                            prospectId={item?.prospect_id || item?.id}
                            clientId={item?.client_id}
                            phone={item?.telephone}
                            title=""
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </span>
    );
}
